import { Check, CircleX, Pencil, RotateCcw, Truck, type LucideIcon } from 'lucide-react';
import { useAuth } from '@platform/auth-client';
import { ORDERS_WRITE_ROLE } from '../customers/customers.ts';
import {
  isBeforeDispatch,
  isCancelable,
  isEditableAtCarrier,
  ORDERS_CONFIRM_ROLE,
  ORDERS_DISPATCH_ROLE,
  type Order,
} from './orders.ts';

export type OrderActionKey = 'confirm' | 'dispatch' | 'reopen' | 'edit' | 'cancel';

export type AvailableOrderAction = {
  readonly key: OrderActionKey;
  readonly labelKey: string;
  readonly Icon: LucideIcon;
};

type ActionRule = AvailableOrderAction & { readonly appliesTo: (order: Order) => boolean };

export const useAvailableOrderActions = (acceptsChanges: (carrierCode: string) => boolean) => {
  const auth = useAuth();
  const mayConfirm = auth.hasRole(ORDERS_CONFIRM_ROLE);
  const mayWrite = auth.hasRole(ORDERS_WRITE_ROLE);
  const mayDispatch = auth.hasRole(ORDERS_DISPATCH_ROLE);
  const rules: readonly ActionRule[] = [
    {
      key: 'confirm',
      labelKey: 'orders.confirm',
      Icon: Check,
      appliesTo: (order) => order.status === 'PENDING' && mayConfirm,
    },
    {
      key: 'dispatch',
      labelKey: 'orders.dispatch',
      Icon: Truck,
      appliesTo: (order) => order.status === 'CONFIRMED' && mayDispatch,
    },
    {
      key: 'reopen',
      labelKey: 'orders.reopen',
      Icon: RotateCcw,
      appliesTo: (order) => order.status === 'CONFIRMED' && mayConfirm,
    },
    {
      key: 'edit',
      labelKey: 'orders.edit',
      Icon: Pencil,
      appliesTo: (order) =>
        (isBeforeDispatch(order) || isEditableAtCarrier(order, acceptsChanges)) &&
        mayWrite &&
        order.piiVisible,
    },
    {
      key: 'cancel',
      labelKey: 'orders.cancel',
      Icon: CircleX,
      appliesTo: (order) => isCancelable(order) && mayWrite,
    },
  ];
  return (order: Order): readonly AvailableOrderAction[] =>
    rules.filter((rule) => rule.appliesTo(order)).map(({ key, labelKey, Icon }) => ({ key, labelKey, Icon }));
};
